import { apiV1 } from "api-v2";

export interface ProjectLeadInfo {
	id: number;
	name: string;
	email: string;
}

export interface ProjectSector {
	id: number;
	name: string;
}

export interface ShortlistProject {
	id: number;
	name: string;
	description: string | null;
	location: string | null;
	project_lead: ProjectLeadInfo | null;
	sectors: ProjectSector[];
	uuid: string;
}

const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getShortlistProjects = async (): Promise<ShortlistProject[]> => {
	const response = await apiV1.get("reports/shortlist/projects");
	return unwrap<ShortlistProject[]>(response.data);
};

/* ---- Available sections metadata ---- */

export interface SectionFieldMeta {
	field: string;
	label: string;
}

export interface SectionMeta {
	fields?: SectionFieldMeta[];
	key: string;
	title: string;
	type: string;
}

export const getAvailableSections = async (): Promise<SectionMeta[]> => {
	const response = await apiV1.get("reports/shortlist/available-sections");
	return unwrap<SectionMeta[]>(response.data);
};

/* ---- Live preview HTML ---- */

export interface PreviewPageHtml {
	html: string;
	label: string;
}

export interface ShortlistPreviewHtmlResponse {
	additional_text_html: string | null;
	cover_html: string | null;
	design_schema: Record<string, unknown>;
	footer_html: string | null;
	header_html: string | null;
	pages: PreviewPageHtml[];
	project_details_html: string | null;
}

export interface ShortlistPreviewHtmlParams {
	additional_text?: string;
	company_ids?: number[];
	cover_created_by_role?: string;
	cover_page_mode?: string;
	cover_presented_to?: string;
	cover_presented_to_role?: string;
	cover_project_name?: string;
	cover_show_logo?: boolean | null;
	cover_supplier_role?: string;
	design_schema?: Record<string, unknown>;
	group_id?: number | string;
	include_project_details?: boolean;
	project_description_override?: string;
	project_id?: number | null;
	project_visible_fields?: Record<string, boolean>;
	search_sector_ids?: number[];
	visible_fields?: Record<string, string[]>;
	visible_sections?: string[];
}

export const getShortlistPreviewHtml = async (
	params: ShortlistPreviewHtmlParams
): Promise<ShortlistPreviewHtmlResponse> => {
	const response = await apiV1.post("reports/shortlist/preview-html", params);
	return unwrap<ShortlistPreviewHtmlResponse>(response.data);
};

/* ---- User presets CRUD ---- */

export interface ShortlistPreset {
	id: number;
	name: string;
	additional_text: string | null;
	company_id: number;
	cover_page: { file_name: string; original_filename: string | null } | null;
	cover_page_file_id: number | null;
	created_at: string;
	description: string | null;
	include_additional_text: boolean;
	include_cover_page: boolean;
	settings_snapshot: Record<string, unknown>;
	settings_type: string;
	updated_at: string;
	user_id: number;
	uuid: string;
}

export interface CreatePresetPayload {
	name: string;
	additional_text?: string | null;
	cover_page_file_id?: number | null;
	description?: string;
	include_additional_text?: boolean;
	include_cover_page?: boolean;
	settings_snapshot: Record<string, unknown>;
	settings_type: string;
}

export type UpdatePresetPayload = Partial<CreatePresetPayload>;

const PRESETS_BASE = "reports/shortlist/presets";

export const getShortlistPresets = async (): Promise<ShortlistPreset[]> => {
	const response = await apiV1.get(PRESETS_BASE);
	return unwrap<ShortlistPreset[]>(response.data);
};

export const getShortlistPreset = async (uuid: string): Promise<ShortlistPreset> => {
	const response = await apiV1.get(`${PRESETS_BASE}/${uuid}`);
	return unwrap<ShortlistPreset>(response.data);
};

export const createShortlistPreset = async (
	payload: CreatePresetPayload
): Promise<ShortlistPreset> => {
	const response = await apiV1.post(PRESETS_BASE, payload);
	return unwrap<ShortlistPreset>(response.data);
};

export const updateShortlistPreset = async (
	uuid: string,
	payload: UpdatePresetPayload
): Promise<ShortlistPreset> => {
	const response = await apiV1.put(`${PRESETS_BASE}/${uuid}`, payload);
	return unwrap<ShortlistPreset>(response.data);
};

export const deleteShortlistPreset = async (uuid: string): Promise<void> => {
	await apiV1.delete(`${PRESETS_BASE}/${uuid}`);
};
